import type { Map } from 'mapbox-gl';
import { useCallback } from 'react';
import factoriesGroup from '@/assets/img/factoryGroup.png';
// Founder 2026-09-21 (round 2): ring-less farmers GROUP icon (farmerGroup.png
// with the white ring + tail + shadow made transparent; same 78x91 canvas,
// disc and glyph). The original farmerGroup.png stays on disk (S2).
import farmersGroup from '@/assets/img/farmerGroup-noring.png';
// Founder 2026-09-21: ring-less pin icons (same 72x81 canvas, same disc,
// glyph and anchor; the white teardrop and shadow made transparent). The
// originals stay on disk (S2). forests-group now reuses the ring-less tree
// icon (was tree.png with a ring).
import farmerPng from '@/assets/svg/farmer-noring.png';
import farmersPng from '@/assets/svg/farmers.png';
import forestPng from '@/assets/svg/tree-noring.png';
import forestsPng from '@/assets/svg/trees.png';

export const useMapImages = () => {
  const loadMarkerImage = useCallback((map: Map, src: string, id: string): Promise<void> => {
    if (map.hasImage(id)) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      map.loadImage(src, (error, image) => {
        if (error || !image) {
          reject(error ?? new Error(`Failed to load map image: ${id}`));
          return;
        }
        map.addImage(id, image);
        resolve();
      });
    });
  }, []);

  const loadAllMapImages = useCallback(
    (map: Map): Promise<void> => {
      const farmersMarker = loadMarkerImage(map, farmerPng, 'farmers-marker');
      const farmersClusters = loadMarkerImage(map, farmersPng, 'farmers-clusters');
      const forestsMarker = loadMarkerImage(map, forestPng, 'forests-marker');
      const forestsClusters = loadMarkerImage(map, forestsPng, 'forests-clusters');
      const farmersGroupImg = loadMarkerImage(map, farmersGroup, 'farmers-group');
      const factoriesGroupImg = loadMarkerImage(map, factoriesGroup, 'factories-group');
      const forestsGroupImg = loadMarkerImage(map, forestPng, 'forests-group');

      return Promise.all([
        farmersMarker,
        farmersClusters,
        forestsMarker,
        forestsClusters,
        farmersGroupImg,
        factoriesGroupImg,
        forestsGroupImg,
      ]).then(() => undefined);
    },
    [loadMarkerImage],
  );

  const setupMapCursor = useCallback((map: Map) => {
    const layers = [
      'factories-group',
      'farmers-group',
      'forests-group',
      'farmers-clusters',
      'forests-clusters',
      'farmers-marker',
      // 2026-09-17: tree pins are interactive too (photo popup) - same cursor.
      // 2026-09-21: and the yellow single-tree dots below the pin zoom.
      'forests-dots',
      'forests-marker',
    ];

    layers.forEach((layer) => {
      map?.on('mouseenter', layer, () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map?.on('mouseleave', layer, () => {
        map.getCanvas().style.cursor = '';
      });
    });
  }, []);

  return {
    loadAllMapImages,
    setupMapCursor,
  };
};
